import { useEffect, useState } from 'react';
import { ArrowLeft, Save, UserRound } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Input } from '@/Components/ui/Input';
import { PageHeader } from '@/Components/data/PageHeader';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import responsavelService, { type ResponsavelInput } from '@/services/responsavel';

const FORM_VAZIO: ResponsavelInput = {
  nome: '',
  cpf: '',
  cargo: '',
  email: '',
  telefone: '',
  ativo: true,
};

export default function ResponsavelForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const [form, setForm] = useState<ResponsavelInput>(FORM_VAZIO);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    responsavelService.buscarPorId(id)
      .then((data) => setForm({
        nome: data.nome,
        cpf: data.cpf,
        cargo: data.cargo || '',
        email: data.email || '',
        telefone: data.telefone || '',
        ativo: data.ativo,
      }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    if (!form.nome || !form.cpf || !form.cargo) return;
    setSalvando(true);
    try {
      if (id) {
        await responsavelService.atualizar(id, form);
      } else {
        await responsavelService.criar(form);
      }
      navigate('/producao/responsaveis');
    } catch (error) {
      console.error('Erro ao salvar responsável', error);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader
        icon={<UserRound size={21} strokeWidth={2} />}
        title={editando ? 'Editar Responsável' : 'Novo Responsável'}
        description={editando ? 'Atualize os dados cadastrais do responsável.' : 'Preencha os dados para incluir um novo responsável.'}
        actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/responsaveis')}><ArrowLeft size={16} /> Voltar</Button>}
      />

      <Card className="nx-page-card nx-sngpc-form-card">
        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header">
            <h2>Identificação</h2>
          </div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Nome *" htmlFor="responsavel-nome" className="nx-sngpc-field--full">
              <Input id="responsavel-nome" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} disabled={loading} required />
            </Field>
            <Field label="CPF *" htmlFor="responsavel-cpf">
              <Input id="responsavel-cpf" value={form.cpf || ''} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" disabled={loading} required />
            </Field>
            <Field label="Cargo *" htmlFor="responsavel-cargo">
              <Input id="responsavel-cargo" value={form.cargo || ''} onChange={(e) => setForm({ ...form, cargo: e.target.value })} disabled={loading} required />
            </Field>
            <Field label="E-mail" htmlFor="responsavel-email">
              <Input id="responsavel-email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={loading} />
            </Field>
            <Field label="Telefone" htmlFor="responsavel-telefone">
              <Input id="responsavel-telefone" value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })} placeholder="(00) 00000-0000" disabled={loading} />
            </Field>
            <label className="nx-sngpc-checkbox-field">
              <Checkbox checked={form.ativo ?? true} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} disabled={loading} />
              <span>Ativo</span>
            </label>
          </div>
        </section>
      </Card>

      <FormStickyFooter
        cancelLabel="Cancelar"
        onCancel={() => navigate('/producao/responsaveis')}
        saveLabel="Salvar"
        loading={salvando}
        saveIcon={<Save size={16} />}
      />
    </form>
  );
}
